/* ----------------- React --------------- */
import { useState } from "react";

import { AppReviewDialog } from "@features/feedback/";
import { useSession } from "@features/session";
import { SessionConfigDialog } from "@features/session-config";
/* ----------------- UI --------------- */
import { BottomFab, Box, useToast } from "@memorang/ui";
import { CustomAppbar, DateTimePicker } from "components/index";
import { HomeView } from "./HomeView";

import { DATE_FORMAT } from "@memorang/constants/src/date";
import { DateTime } from "luxon";

import { useExamContext } from "@features/exam";
/* ----------------- Hooks --------------- */
import { useDevice } from "@hooks/useDevice";
import { useHome } from "../hooks/useHome";

/* ----------------- i18n --------------- */
import { useTranslation } from "react-i18next";

/* ----------------- Helpers --------------- */
import { isWeb } from "@helpers/platform";

/* ----------------- Router --------------- */
import { useLocalSearchParams, useRouter } from "expo-router";

import { events } from "@constants/tracking";
import { CollectUserDetailsDialog } from "@features/account";
import { FreeQuizSessionDialog } from "@features/auth";
/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";
import { OLXExamEligibilityDialog } from "../../../components/OLXExamEligibilityDialog";
import { PurchaseDialog } from "./PurchaseDialog";

export const HomeRouteContainer = () => {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const {
		currentExamContext: { examDate },
		updateExamDate,
	} = useExamContext();
	const { isMobile } = useDevice();
	const { startDiagnostic, showPurchase } = useLocalSearchParams<{
		startDiagnostic: string;
		showPurchase?: string;
	}>();
	const { setParams } = useRouter();
	const toggleShowPurchaseDialog = () => {
		if (showPurchase) {
			setParams({
				showPurchase: "false",
			});
		}
		setShowPurchaseDialog((prev) => !prev);
	};

	const [showPurchaseDialog, setShowPurchaseDialog] = useState(
		showPurchase === "true",
	);

	const router = useRouter();

	const {
		sections,
		headerData,
		showPurchaseDialog: showPracticeTestPurchaseDialog,
		toggleShowPurchaseDialog: toggleShowPracticeTestPurchaseDialog,
		olxExamEligibility,
		setOlxExamEligibility,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		isLoading,
		showDatePicker,
		toggleShowDatePicker,
		hasAccess,
		exams,
		recommendationItems,
		selectedWeakTags,
		setSelectedWeakTags,
	} = useHome();

	const { handleDirectSessionStart } = useSession();

	const handleShowPurchase = () => {
		if (isWeb) {
			toggleShowPurchaseDialog?.();
		} else {
			router.push({
				pathname: "/purchase",
				params: {
					productType: "STUDY_PACK",
				},
			});
		}
	};

	const handleQuickStudy = () => {
		trackCustomEvent({
			eventName: events.studyFabClicked,
		});
		handleDirectSessionStart();
	};

	const showBottomFab = isMobile && !isLoading;

	const handleSelectTopic = (topicId: string) => {
		handleDirectSessionStart({ moduleId: topicId });
	};

	const showFreeQuizDialog = startDiagnostic === "true";

	const showOlxExamEligibilityDialog = !!olxExamEligibility;
	const showSessionConfigDialog = !!selectedWeakTags?.length;

	const handleCloseSessionConfigDialog = () => {
		setSelectedWeakTags(undefined);
	};

	return (
		<Box position="relative" flexGrow={1} height={"100%"}>
			<CustomAppbar
				mode={isMobile ? "small" : "medium"}
				align="flex-start"
				showCommandBar={isWeb}
				options={{
					headerTitle: isWeb && isMobile ? "" : t("appbar.home.title"),
					subtitle: isWeb || isMobile ? "" : t("appbar.home.subtitle"),
				}}
				showHambugerMenu
			/>
			<HomeView
				headerData={headerData}
				sections={sections}
				exams={exams}
				handleSelectTopic={handleSelectTopic}
				hasAccess={hasAccess}
				handleShowPurchase={handleShowPurchase}
				recommendationItems={recommendationItems}
				showPurchaseDialog={showPracticeTestPurchaseDialog}
				toggleShowPurchaseDialog={toggleShowPracticeTestPurchaseDialog}
			/>
			{showBottomFab && (
				<BottomFab
					icon={"play-outline"}
					label={t("dashboard.header.quickStudy")}
					onPress={handleQuickStudy}
					bottom={16}
				/>
			)}

			{!isWeb && <AppReviewDialog />}

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => {
						router.setParams({
							startDiagnostic: "false",
						});
					}}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType="STUDY_PACK"
				/>
			)}
			{showOlxExamEligibilityDialog && (
				<OLXExamEligibilityDialog
					visible={showOlxExamEligibilityDialog}
					handleClose={() => setOlxExamEligibility?.(null)}
					startDateTime={olxExamEligibility?.startDate}
					endDateTime={olxExamEligibility?.endDate}
				/>
			)}
			{showCollectUserDetailsDialog && toggleShowCollectUserDetailsDialog && (
				<CollectUserDetailsDialog
					visible={showCollectUserDetailsDialog}
					handleClose={toggleShowCollectUserDetailsDialog}
				/>
			)}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					selectedTagIds={selectedWeakTags}
					handleClose={handleCloseSessionConfigDialog}
				/>
			) : null}
			{showDatePicker ? (
				<DateTimePicker
					isVisible
					onCancel={toggleShowDatePicker}
					initialDate={examDate}
					onConfirm={(date) => {
						if (!isWeb) {
							toggleShowDatePicker();
						}
						updateExamDate(date);
						const formattedDate =
							DateTime.fromJSDate(date).toFormat(DATE_FORMAT);
						showToast({
							message: t("account.examDateUpdated", { date: formattedDate }),
							duration: 1000,
						});
					}}
				/>
			) : null}
		</Box>
	);
};
